import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import appDataManager from '../../services/AppDataManager';

// Kritische Benachrichtigung: serverseitige Portal-Events mit priority 'critical'
// ('overdue_ride' = Legacy-Typ des früheren client-seitigen Monitorings)
export const isCriticalNotification = (notification) =>
  notification?.data?.priority === 'critical' || notification?.type === 'overdue_ride';

// Echtzeit-Listener über AppDataManager starten
let cleanupCallback = null;

export const subscribeToNotifications = ({ userId }) => (dispatch) => {
  // Bestehenden Callback aufräumen
  if (cleanupCallback) {
    cleanupCallback();
    cleanupCallback = null;
  }

  if (!userId) return;

  // Callback registrieren der Redux-State updatet wenn AppDataManager neue Daten hat
  cleanupCallback = appDataManager.registerCallback('notifications', (notifications) => {
    dispatch(notificationsSlice.actions.setNotifications(notifications));
  });

  // Listener im AppDataManager starten
  appDataManager.setupNotificationsListener(userId);
};

export const unsubscribeFromNotifications = () => () => {
  if (cleanupCallback) {
    cleanupCallback();
    cleanupCallback = null;
  }
};

// Async thunks
export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);

      await updateDoc(notificationRef, {
        read: true
      });

      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (userId, { rejectWithValue, getState }) => {
    try {
      const { notifications } = getState().notifications;
      const unreadNotifications = notifications.filter(notification =>
        !notification.read && notification.userId === userId
      );

      const updatePromises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'notifications', notification.id), {
          read: true
        })
      );

      await Promise.all(updatePromises);

      return unreadNotifications.map(notification => notification.id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAllNotifications = createAsyncThunk(
  'notifications/deleteAll',
  async (userId, { rejectWithValue, getState }) => {
    try {
      const { notifications } = getState().notifications;
      const userNotifications = notifications.filter(notification =>
        notification.userId === userId
      );

      const deletePromises = userNotifications.map(notification =>
        deleteDoc(doc(db, 'notifications', notification.id))
      );

      await Promise.all(deletePromises);

      return userNotifications.map(notification => notification.id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  criticalCount: 0,
  isLoading: false,
  error: null
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotificationsError: (state) => {
      state.error = null;
    },
    setNotifications: (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.notifications = action.payload;
      state.unreadCount = action.payload.filter(n => !n.read).length;
      state.criticalCount = action.payload.filter(n =>
        !n.read && isCriticalNotification(n)
      ).length;
    }
  },
  extraReducers: (builder) => {
    builder
      // Mark notification as read — optimistisches Update
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && !notification.read) {
          notification.read = true;
          state.unreadCount -= 1;
          if (isCriticalNotification(notification)) {
            state.criticalCount -= 1;
          }
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Mark all notifications as read — optimistisches Update
      .addCase(markAllNotificationsAsRead.fulfilled, (state, action) => {
        action.payload.forEach(id => {
          const notification = state.notifications.find(n => n.id === id);
          if (notification) {
            notification.read = true;
          }
        });
        state.unreadCount = state.notifications.filter(n => !n.read).length;
        state.criticalCount = state.notifications.filter(n =>
          !n.read && isCriticalNotification(n)
        ).length;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete notification — optimistisches Update
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const deleted = state.notifications.find(n => n.id === action.payload);
        state.notifications = state.notifications.filter(n => n.id !== action.payload);
        if (deleted && !deleted.read) {
          state.unreadCount -= 1;
          if (isCriticalNotification(deleted)) {
            state.criticalCount -= 1;
          }
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete all notifications
      .addCase(deleteAllNotifications.fulfilled, (state, action) => {
        state.notifications = state.notifications.filter(
          n => !action.payload.includes(n.id)
        );
        state.unreadCount = state.notifications.filter(n => !n.read).length;
        state.criticalCount = state.notifications.filter(n =>
          !n.read && isCriticalNotification(n)
        ).length;
      })
      .addCase(deleteAllNotifications.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const { clearNotificationsError } = notificationsSlice.actions;
export default notificationsSlice.reducer;
